// Integración del asistente IA DataRete para proyectos Vue CLI + Vuex + vue-router.
//
// ESTE ARCHIVO ES IGUAL EN TODOS LOS PROYECTOS: se copia tal cual a src/composables/.
// Lo que cambia entre proyectos va en los .env (VUE_APP_AI_*), no aquí.
// Fuente: repositorio widget-ai, integrations/vue-cli/useAssistenteIA.js
//
// Uso (una sola vez, en el layout con sesión iniciada, p. ej. AppLayout.vue):
//
//   import { useAssistenteIA } from "@/composables/useAssistenteIA";
//   export default {
//     setup() {
//       useAssistenteIA();
//       // Opcional, si el proyecto tiene más información:
//       // useAssistenteIA({ pages: infoPagina, getContext: () => ({ tipoProdotto }) });
//     },
//   };
//
// .env.development / .env.production:
//   VUE_APP_AI_ENABLED=true|false
//   VUE_APP_AI_APP=prestitosi                    identificador del proyecto para el gateway
//   VUE_APP_AI_SCRIPT_URL=http://localhost:5174/datarete-ai.js
//   VUE_APP_AI_URL=                              gateway IA; vacío = modo demo
//   VUE_APP_AI_LIVELLI=Amministratore            niveles que ven el asistente (separados por comas)
//
// El contexto (página, IDs de la URL, pestaña activa) lo calcula el propio widget a partir
// del router: no hay que publicar nada desde las páginas.

import { computed, onBeforeUnmount, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { AUTH_LOGOUT } from "@/store/actions/auth";

const AI_ABILITATA = process.env.VUE_APP_AI_ENABLED === "true";
const APP = process.env.VUE_APP_AI_APP || "";
const SCRIPT_URL = process.env.VUE_APP_AI_SCRIPT_URL || "";
const API_URL = process.env.VUE_APP_AI_URL || "";
const LIVELLI_ABILITATI = (process.env.VUE_APP_AI_LIVELLI || "Amministratore")
  .split(",")
  .map((livello) => livello.trim())
  .filter(Boolean);

let caricamentoScript = null;

function caricaScript() {
  if (window.DataReteAI) return Promise.resolve(window.DataReteAI);
  if (!caricamentoScript) {
    caricamentoScript = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () =>
        window.DataReteAI
          ? resolve(window.DataReteAI)
          : reject(new Error("window.DataReteAI non disponibile"));
      script.onerror = () => {
        // Permite reintentar más tarde (p. ej. si el servidor estaba caído)
        caricamentoScript = null;
        script.remove();
        reject(new Error(`Impossibile caricare ${SCRIPT_URL}`));
      };
      document.head.appendChild(script);
    });
  }
  return caricamentoScript;
}

const toId = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

/**
 * @param {object} [options]
 * @param {object|function} [options.pages] Nombres legibles de las páginas:
 *   { nombreRuta: { pagina, sezione } } o (route) => ({ pagina, sezione }).
 *   Sin esto se usa meta.title de la ruta o el nombre de la ruta.
 * @param {() => object} [options.getContext] Datos extra de la página actual que el widget
 *   no puede deducir (p. ej. { tipoProdotto }). Nunca datos de clientes.
 */
export function useAssistenteIA(options = {}) {
  const store = useStore();
  const router = useRouter();

  const loggedUser = computed(() => store.getters["loggedUser"] || {});

  const visible = computed(
    () =>
      AI_ABILITATA &&
      Boolean(SCRIPT_URL) &&
      Boolean(store.state.auth.token) &&
      LIVELLI_ABILITATI.includes(loggedUser.value.livelloLogin)
  );

  function logout() {
    store.dispatch(AUTH_LOGOUT).then(() => router.push({ name: "Login" }));
  }

  let attivo = false;

  async function avvia() {
    try {
      const ai = await caricaScript();
      // Puede haber cambiado mientras se cargaba el script
      if (!visible.value || attivo) return;
      ai.init({
        app: APP,
        apiUrl: API_URL,
        getToken: () => store.state.auth.token,
        router,
        pages: options.pages,
        getContext: options.getContext,
        user: {
          id: toId(store.state.auth.IdUser),
          livelloLogin: loggedUser.value.livelloLogin,
          name: loggedUser.value.nome,
        },
        lang: "it",
        onSessionExpired: logout,
      });
      attivo = true;
    } catch (error) {
      console.error("Assistente IA:", error);
    }
  }

  function ferma() {
    if (!attivo) return;
    window.DataReteAI?.destroy();
    attivo = false;
  }

  watch(visible, (mostra) => (mostra ? avvia() : ferma()), { immediate: true });
  onBeforeUnmount(ferma);
}
